import * as React from "react";
import * as Article from "@elevio/kb-kit/lib/components/article";
import { useTranslation } from "@elevio/kb-kit/lib/hooks";
import { getAssetURL } from "@elevio/kb-kit/lib/utils/globals";
import { getHeader } from "./home";
import PageLayout from "../components/layouts/Page";
import Footer from "../components/Footer";
import WithAsideLayout, { Aside, Main, MainHeader, MainBody, } from "../components/layouts/WithAside";
import ArticleList from "../components/ArticleList";
import ArticleMeta from "../components/ArticleMeta";
import ArticleActions from "../components/ArticleActions";
import ArticleFeedback from "../components/ArticleFeedback";
import { fetchAgencyFlags } from "../requests/agency-flags";
import { privateSubmissionUrl } from "../utils/privateSubmissionUrl";
function Page() {
    const { t } = useTranslation();
    const [showViewAllRequests, setShowViewAllRequests] = React.useState(false);
    React.useEffect(() => {
        const AGENCY_ID = 171;
        let cancelled = false;
        fetchAgencyFlags(AGENCY_ID).then((data) => {
            if (!cancelled) {
                setShowViewAllRequests(Boolean(data === null || data === void 0 ? void 0 : data.isPublicMapEnabled));
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);
    const openPrivateSubmission = (path) => {
        const url = privateSubmissionUrl(path);
        if (!url) {
            return;
        }
        const newWindow = window.open(url, "_blank");
        if (newWindow) {
            newWindow.opener = null;
        }
    };
    const handleSubmitRequest = (e) => {
        e.preventDefault();
        e.stopPropagation();
        openPrivateSubmission("create");
    };
    const handleViewMyRequests = (e) => {
        e.preventDefault();
        e.stopPropagation();
        openPrivateSubmission("listing");
    };
    const handleViewAllRequests = (e) => {
        e.preventDefault();
        e.stopPropagation();
        openPrivateSubmission("public-map");
    };
    React.useEffect(() => {
        const mainBody = document.querySelector('.main-body');
        if (!mainBody)
            return;
        const patchHeadingOrder = () => {
            const headings = Array.from(mainBody.querySelectorAll('h1, h2, h3, h4, h5, h6'));
            if (headings.length === 0)
                return;
            let previousLevel = 1;
            headings.forEach((heading) => {
                const currentLevel = Number(heading.tagName.replace('H', ''));
                if (!Number.isInteger(currentLevel) || currentLevel < 1 || currentLevel > 6)
                    return;
                const maxAllowedLevel = Math.min(previousLevel + 1, 6);
                const nextLevel = currentLevel > maxAllowedLevel ? maxAllowedLevel : currentLevel;
                if (nextLevel !== currentLevel) {
                    const replacement = document.createElement(`h${nextLevel}`);
                    heading.getAttributeNames().forEach((attrName) => {
                        const attrValue = heading.getAttribute(attrName);
                        if (attrValue !== null) {
                            replacement.setAttribute(attrName, attrValue);
                        }
                    });
                    while (heading.firstChild) {
                        replacement.appendChild(heading.firstChild);
                    }
                    heading.replaceWith(replacement);
                    previousLevel = nextLevel;
                    return;
                }
                previousLevel = currentLevel;
            });
        };
        const patchUnnamedLinks = () => {
            mainBody.querySelectorAll('a[href]').forEach((anchor) => {
                var _a;
                if ((_a = anchor.textContent) === null || _a === void 0 ? void 0 : _a.trim())
                    return;
                const href = anchor.getAttribute('href') || 'article reference';
                const readableHref = href.replace(/^https?:\/\//i, '');
                anchor.setAttribute('aria-label', `Link to ${readableHref}`);
                if (!anchor.getAttribute('title')) {
                    anchor.setAttribute('title', readableHref);
                }
            });
        };
        const patchImagesWithoutAlt = () => {
            mainBody.querySelectorAll('img').forEach((image) => {
                var _a, _b, _c, _d;
                if (image.hasAttribute('alt'))
                    return;
                const title = (_a = image.getAttribute('title')) === null || _a === void 0 ? void 0 : _a.trim();
                const figureCaption = (_d = (_c = (_b = image.closest('figure')) === null || _b === void 0 ? void 0 : _b.querySelector('figcaption')) === null || _c === void 0 ? void 0 : _c.textContent) === null || _d === void 0 ? void 0 : _d.trim();
                const fallbackAlt = title || figureCaption || 'Article image';
                image.setAttribute('alt', fallbackAlt);
            });
        };
        const patchLowContrastRichText = () => {
            mainBody
                .querySelectorAll('strong > span[data-contrast="none"][lang="EN-US"] > span[data-ccp-charstyle="Hyperlink"]')
                .forEach((element) => {
                element.style.setProperty('color', '#123b73', 'important');
            });
            mainBody
                .querySelectorAll('span[data-ccp-parastyle="Normal (Web)"] > span')
                .forEach((element) => {
                if (window.getComputedStyle(element).color !== 'rgb(209, 72, 65)')
                    return;
                element.style.setProperty('color', '#8b1f1a', 'important');
            });
        };
        const patchRichContentAccessibility = () => {
            patchHeadingOrder();
            patchUnnamedLinks();
            patchImagesWithoutAlt();
            patchLowContrastRichText();
        };
        patchRichContentAccessibility();
        const observer = new MutationObserver(patchRichContentAccessibility);
        observer.observe(mainBody, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);
    return (<PageLayout header={getHeader()} footer={<Footer />}>
      {/* <div className="herriman-logo-wrapper">
          <img className="herriman-logo" src={accessHerrimanLogoUrl} alt="herriman-logo-center" />
        </div> */}
      <section className="button-container button-container--three section-gap">
        <div className="button-wrapper">
          <button type="button" className="cta-request" onClick={handleSubmitRequest}>
            <img src={getAssetURL("images/request.svg")} alt="request icon" aria-hidden/>
            Submit a Request
          </button>
        </div>
        <div className="button-wrapper">
          <button type="button" className="cta-request" onClick={handleViewMyRequests}>
            <img src={getAssetURL("images/inbox.svg")} alt="inbox icon" aria-hidden/>
            View My Requests
          </button>
        </div>
        {showViewAllRequests ? (<div className="button-wrapper">
            <button type="button" className="cta-request" onClick={handleViewAllRequests}>
              <img src={getAssetURL("images/map.svg")} alt="map icon" aria-hidden/>
              View All Requests
            </button>
          </div>) : null}
      </section>
      <WithAsideLayout>
        <Main>
          <MainHeader>
            <ArticleActions />
            <h1 className="main-title" data-testid="article-title">
              <Article.Title />
            </h1>
            <ArticleMeta />
          </MainHeader>

          <MainBody>
            <Article.Body />
          </MainBody>
          <ArticleFeedback />
        </Main>

        <Aside>
          <ArticleList isRelated isAside/>
        </Aside>
      </WithAsideLayout>
    </PageLayout>);
}
export default Page;
